import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  QBox,
  QButton,
  QCloseButton,
  QDatepicker,
  QFormControl,
  QInput,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  QTextarea,
} from '@qualio/ui-components';

import { ChangeDueDateForm, ChangeDueDateFormSchema } from './formHelpers';
import { formatDatePickerComponentDateToString } from '../../utils/datetimeUtils';
import { ReasonForChangeInputLabel } from '../../displayStrings';

type ChangeDueDateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: ChangeDueDateForm) => void | Promise<void>;
  isLoading?: boolean;
  currentDueDate: string;
  eventName: string;
};

const defaultValues: ChangeDueDateForm = { due_date: '', reason: '' };

const ChangeDueDateModal: React.FC<ChangeDueDateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  currentDueDate,
  eventName,
}) => {
  const { t } = useTranslation();

  const formMethods = useForm<ChangeDueDateForm>({
    mode: 'onSubmit',
    resolver: zodResolver(ChangeDueDateFormSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods;

  // Date pickers respect calendar minDate, but a fresh `new Date()` on each render
  // creates a new reference. Memoize so QDatepicker doesn't re-render unnecessarily.
  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    if (!isOpen) {
      reset(defaultValues);
    }
  }, [isOpen, reset]);

  const handleClose = useCallback(() => {
    reset(defaultValues);
    onClose();
  }, [onClose, reset]);

  const submit = handleSubmit(async (data) => {
    await onSubmit(data);
    handleClose();
  });

  return (
    <QModal isOpen={isOpen} onClose={handleClose} size="xl">
      <QModalHeader>
        <QText>{t('events.actions.changeDueDate')}</QText>
        <QCloseButton onClick={handleClose} data-cy="change-due-date-close-button" />
      </QModalHeader>

      <QModalBody>
        <QStack direction="column" color="gray.700" spacing={4}>
          <QText color="gray.800" data-cy="change-due-date-event-name">
            {eventName}
          </QText>

          <QFormControl label={t('common.labels.currentDueDate')}>
            <QBox data-cy="change-due-date-current">
              <QInput value={currentDueDate} isReadOnly isDisabled />
            </QBox>
          </QFormControl>

          <QFormControl
            label={t('common.labels.newDueDate')}
            isRequired
            isInvalid={!!errors.due_date}
            error={errors.due_date?.message}
            data-cy="change-due-date-new"
          >
            <Controller
              control={control}
              name="due_date"
              render={({ field: { value, onChange } }) => (
                <QDatepicker
                  value={value || undefined}
                  onChange={(date) => onChange(date ? formatDatePickerComponentDateToString(date) : '')}
                  minDate={today}
                />
              )}
            />
          </QFormControl>

          <QFormControl
            label={ReasonForChangeInputLabel}
            isRequired
            isInvalid={!!errors.reason}
            error={errors.reason?.message}
            data-cy="change-due-date-reason"
          >
            <Controller
              control={control}
              name="reason"
              render={({ field: { ref: _ref, ...field } }) => (
                <QTextarea {...field} placeholder={t('events.modals.changeDueDateReasonPlaceholder')} />
              )}
            />
          </QFormControl>
        </QStack>
      </QModalBody>

      <QModalActions>
        <QButton onClick={handleClose} variant="outline" isDisabled={isLoading}>
          {t('common.actions.cancel')}
        </QButton>
        <QButton
          onClick={submit}
          isLoading={isLoading}
          isDisabled={isLoading}
          data-cy="change-due-date-submit"
          data-metrics="change-due-date-submit"
        >
          {t('events.actions.changeDueDate')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default ChangeDueDateModal;
